<template>
  <header class="topbar">
    <div class="container topbar-inner">
      <button
        class="nav-toggle"
        type="button"
        :aria-expanded="mobileNavOpen ? 'true' : 'false'"
        aria-label="打开菜单"
        @click="toggleMobileNav"
      >
        <span class="nav-toggle-icon" aria-hidden="true" />
      </button>

      <div class="brand" aria-label="Dose">
        <img :src="logo" alt="Dose logo" class="brand-logo" />
      </div>

      <nav class="nav" aria-label="主导航">
        <template v-for="item in navItemsResolved">
          <div v-if="item.children && item.children.length" :key="item.label + '-group'" class="nav-item has-dropdown">
            <a
              class="nav-link"
              :class="{ active: item.label === 'TREATMENTS' && isTreatmentsSectionActive }"
              href="#"
              @click.prevent="item.to ? go(item.to) : null"
            >
              <span class="nav-label">{{ item.label }}</span>
              <span class="nav-caret" aria-hidden="true" />
            </a>
            <div class="nav-dropdown" role="menu" :aria-label="item.label + ' submenu'">
              <a
                v-for="child in item.children"
                :key="item.label + '-' + child.label"
                class="nav-dropdown-link"
                href="#"
                role="menuitem"
                @click.prevent="child.to ? go(child.to) : null"
              >
                {{ child.label }}
              </a>
            </div>
          </div>
          <a
            v-else-if="item.to"
            :key="item.label + '-to'"
            class="nav-link"
            :class="{ active: isActive(item.to) }"
            @click.prevent="go(item.to)"
          >
            <span class="nav-label">{{ item.label }}</span>
          </a>
          <a v-else :key="item.label + '-static'" class="nav-link" href="#" @click.prevent>
            <span class="nav-label">{{ item.label }}</span>
          </a>
        </template>
      </nav>

      <div v-if="headerPhone" class="topbar-right" aria-label="phone">
        <a class="phone" :href="'tel:' + telHref">
          <span class="phone-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" width="14" height="14" focusable="false">
              <path
                d="M6.62 10.79a15.05 15.05 0 0 0 6.59 6.59l2.2-2.2a1 1 0 0 1 1.01-.24c1.12.37 2.33.57 3.58.57a1 1 0 0 1 1 1V20a1 1 0 0 1-1 1C10.07 21 3 13.93 3 5a1 1 0 0 1 1-1h3.5a1 1 0 0 1 1 1c0 1.25.2 2.46.57 3.58a1 1 0 0 1-.24 1.01l-2.2 2.2Z"
                fill="#0c0b06"
              />
            </svg>
          </span>
          <span class="phone-text">{{ headerPhone }}</span>
        </a>
      </div>
    </div>

    <div v-if="mobileNavOpen" class="drawer-overlay" role="presentation" @click="closeMobileNav" />
    <aside class="mobile-drawer" :class="{ open: mobileNavOpen }" aria-label="app Menu">
      <div class="drawer-head">
        <button class="drawer-close" type="button" aria-label="close Menu" @click="closeMobileNav">
          ×
        </button>
      </div>
      <nav class="drawer-nav">
        <template v-for="item in navItemsResolved">
          <div v-if="item.children && item.children.length" :key="item.label + '-m-group'" class="drawer-group">
            <div class="drawer-row">
              <a
                class="drawer-link drawer-link-main"
                :class="{ active: item.label === 'TREATMENTS' && isTreatmentsSectionActive }"
                href="#"
                @click.prevent="item.to ? goFromDrawer(item.to) : closeMobileNav()"
              >
                <span>{{ item.label }}</span>
              </a>
              <button
                class="drawer-caret-btn"
                type="button"
                :aria-expanded="isDrawerGroupOpen(item.label) ? 'true' : 'false'"
                :aria-label="'Toggle ' + item.label"
                @click="toggleDrawerGroup(item.label)"
              >
                <span class="drawer-caret" aria-hidden="true" />
              </button>
            </div>
            <div v-if="isDrawerGroupOpen(item.label)" class="drawer-sub">
              <a
                v-for="child in item.children"
                :key="item.label + '-m-' + child.label"
                class="drawer-sublink"
                href="#"
                @click.prevent="child.to ? goFromDrawer(child.to) : closeMobileNav()"
              >
                {{ child.label }}
              </a>
            </div>
          </div>
          <a
            v-else-if="item.to"
            :key="item.label + '-m-to'"
            class="drawer-link"
            :class="{ active: isActive(item.to) }"
            href="#"
            @click.prevent="goFromDrawer(item.to)"
          >
            <span>{{ item.label }}</span>
          </a>
          <a
            v-else
            :key="item.label + '-m-static'"
            class="drawer-link"
            href="#"
            @click.prevent="closeMobileNav"
          >
            <span>{{ item.label }}</span>
          </a>
        </template>
      </nav>
    </aside>
  </header>
</template>

<script>
import { fetchPublicTreatments } from '../api/treatment'
import { fetchPublicContact } from '../api/contact'

export default {
  name: 'TopBar',
  data() {
    return {
      mobileNavOpen: false,
      drawerGroupsOpen: {},
      logo: require('../assets/images/logo.png'),
      /** GET /web/contact/public → phone */
      headerPhone: '',
      /** 接口失败时：仅保留入口，子项为空则下拉无条目 */
      treatmentChildrenFallback: [{ label: 'View all treatments', to: '/treatments' }],
      /** 来自后台 treatments 公开列表，非 null 表示已尝试拉取 */
      treatmentChildrenFromApi: null,
      navItems: [
        { label: 'HOME', to: '/' },
        { label: 'ABOUT US', to: '/about' },
        {
          label: 'TREATMENTS',
          to: '/treatments',
          children: [],
        },
        { label: 'BLOG', to: '/blog' },
        { label: 'NEWS', to: '/news' },
        { label: 'PRICING', to: '/pricing' },
        { label: 'FAQ', to: '/faq' },
        { label: 'CONTACT US', to: '/contact' },
      ],
    }
  },
  computed: {
    treatmentDropdownChildren() {
      if (this.treatmentChildrenFromApi && this.treatmentChildrenFromApi.length) {
        return this.treatmentChildrenFromApi
      }
      if (this.treatmentChildrenFromApi && this.treatmentChildrenFromApi.length === 0) {
        return []
      }
      return this.treatmentChildrenFallback
    },
    navItemsResolved() {
      return this.navItems.map((item) => {
        if (item.label === 'TREATMENTS') {
          return { ...item, children: this.treatmentDropdownChildren }
        }
        return item
      })
    },
    isTreatmentsSectionActive() {
      const p = this.$route && this.$route.path
      return p === '/treatments' || (p && p.indexOf('/treatments/detail/') === 0)
    },
    telHref() {
      return String(this.headerPhone || '').replace(/\s+/g, '')
    },
  },
  watch: {
    mobileNavOpen(open) {
      document.body.style.overflow = open ? 'hidden' : ''
      if (!open) this.drawerGroupsOpen = {}
    },
  },
  mounted() {
    fetchPublicTreatments()
      .then((list) => {
        this.treatmentChildrenFromApi = (list || []).map((t) => ({
          label: (t.title && String(t.title).trim()) || 'Treatment',
          to: '/treatments/detail/' + encodeURIComponent(t.id),
        }))
      })
      .catch(() => {
        this.treatmentChildrenFromApi = null
      })
    fetchPublicContact()
      .then((data) => {
        this.headerPhone = (data.phone || '').trim()
      })
      .catch(() => {
        this.headerPhone = ''
      })
  },
  beforeDestroy() {
    document.body.style.overflow = ''
  },
  methods: {
    toggleMobileNav() {
      this.mobileNavOpen = !this.mobileNavOpen
    },
    closeMobileNav() {
      this.mobileNavOpen = false
    },
    go(to) {
      if (!this.$router) {
        window.location.hash = `#${to}`
        return
      }
      let loc = to
      if (typeof to === 'string' && to.startsWith('/treatments#')) {
        const i = to.indexOf('#')
        const inner = to.slice(i + 1)
        loc = { path: '/treatments', hash: inner ? `#${inner}` : '' }
      }
      this.$router.push(loc).catch((err) => {
        if (err && err.name !== 'NavigationDuplicated') throw err
      })
    },
    goFromDrawer(to) {
      this.closeMobileNav()
      this.go(to)
    },
    isActive(to) {
      const path = this.$route && this.$route.path
      return path === to
    },
    toggleDrawerGroup(label) {
      this.$set(this.drawerGroupsOpen, label, !this.drawerGroupsOpen[label])
    },
    isDrawerGroupOpen(label) {
      return !!this.drawerGroupsOpen[label]
    },
  },
}
</script>

<style scoped>
.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
}

.topbar {
  position: sticky;
  top: 0;
  z-index: 10;
  background: #ffffff;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}

.topbar-inner {
  height: 78px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.nav-toggle {
  display: none;
  width: 44px;
  height: 44px;
  border: none;
  background: transparent;
  padding: 0;
  cursor: pointer;
  color: #0c0b06;
}

.nav-toggle-icon {
  width: 18px;
  height: 2px;
  background: currentColor;
  display: inline-block;
  position: relative;
}

.nav-toggle-icon::before,
.nav-toggle-icon::after {
  content: '';
  position: absolute;
  left: 0;
  width: 18px;
  height: 2px;
  background: currentColor;
}

.nav-toggle-icon::before {
  top: -6px;
}

.nav-toggle-icon::after {
  top: 6px;
}

.drawer-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.35);
  z-index: 40;
}

.mobile-drawer {
  position: fixed;
  top: 0;
  left: 0;
  height: 100vh;
  width: min(320px, 86vw);
  background: #fff;
  z-index: 50;
  transform: translateX(-100%);
  transition: transform 200ms ease;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.18);
  display: flex;
  flex-direction: column;
}

.mobile-drawer.open {
  transform: translateX(0);
}

.drawer-head {
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 0 16px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}

.drawer-close {
  width: 40px;
  height: 40px;
  border: none;
  background: transparent;
  font-size: 26px;
  line-height: 1;
  cursor: pointer;
  color: #0c0b06;
}

.drawer-nav {
  padding: 12px 0;
  overflow: auto;
}

.drawer-link {
  display: flex;
  align-items: center;
  height: 46px;
  padding: 0 18px;
  text-decoration: none;
  color: #0c0b06;
  font-size: 12px;
  letter-spacing: 0.08em;
  border-bottom: 1px solid rgba(0, 0, 0, 0.04);
  cursor: pointer;
}

.drawer-link:active {
  background: rgba(0, 0, 0, 0.03);
}

.drawer-row {
  display: flex;
  align-items: stretch;
}

.drawer-link-main {
  flex: 1 1 auto;
  border-right: 1px solid rgba(0, 0, 0, 0.04);
}

.drawer-caret-btn {
  width: 52px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  border-bottom: 1px solid rgba(0, 0, 0, 0.04);
  cursor: pointer;
  color:#0c0b06;
}

.drawer-caret-btn:active {
  background: rgba(0, 0, 0, 0.03);
}

.drawer-group {
  display: block;
}

.drawer-caret {
  width: 0;
  height: 0;
  border-left: 4px solid transparent;
  border-right: 4px solid transparent;
  border-top: 5px solid rgba(0, 0, 0, 0.5);
  margin-left: 10px;
  transform: translateY(1px);
}

.drawer-sub {
  padding: 6px 0 10px;
  background: rgba(0, 0, 0, 0.015);
}

.drawer-sublink {
  display: flex;
  align-items: center;
  height: 40px;
  padding: 0 18px 0 30px;
  text-decoration: none;
  color: #0c0b06;
  font-size: 11px;
  letter-spacing: 0.04em;
  cursor: pointer;
}

.drawer-sublink:active {
  background: rgba(0, 0, 0, 0.03);
}

.brand {
  display: flex;
  align-items: center;
  gap: 0;
  flex: 0 0 auto;
}

.brand-logo {
  height: 50px;
  width: auto;
  max-width: 240px;
  object-fit: contain;
}

.nav {
  flex: 1;
  display: flex;
  gap: 34px;
  flex-wrap: nowrap;
  justify-content: center;
}

.nav-item {
  position: relative;
  display: inline-flex;
  align-items: center;
}

.nav-dropdown {
  position: absolute;
  left: 50%;
  top: calc(100% - 2px);
  transform: translateX(-50%);
  min-width: 280px;
  background: #ffffff;
  border: 1px solid rgba(0, 0, 0, 0.06);
  box-shadow: 0 14px 28px rgba(0, 0, 0, 0.14);
  padding: 10px 0;
  opacity: 0;
  visibility: hidden;
  pointer-events: none;
  transition: opacity 0.15s ease, transform 0.15s ease, visibility 0.15s ease;
}

.has-dropdown:hover .nav-dropdown,
.has-dropdown:focus-within .nav-dropdown {
  opacity: 1;
  visibility: visible;
  pointer-events: auto;
  transform: translateX(-50%) translateY(4px);
}

.nav-dropdown-link {
  display: block;
  padding: 10px 16px;
  text-decoration: none;
  color: #0c0b06;
  font-size: 11px;
  letter-spacing: 0.04em;
  cursor: pointer;
  white-space: nowrap;
}

.nav-dropdown-link:hover {
  background: rgba(176, 138, 74, 0.08);
  color: #b08a4a;
}

.nav-link {
  text-decoration: none;
  color: #0c0b06;
  font-size: 11px;
  letter-spacing: 0.08em;
  display: inline-flex;
  align-items: center;
  white-space: nowrap;
  position: relative;
  padding: 8px 2px 10px;
  transition: color 0.18s ease;
  cursor: pointer;
}

.nav-link::after {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  bottom: 2px;
  height: 2px;
  background: #b08a4a;
  opacity: 0;
  transform: scaleX(0.75);
  transform-origin: center;
  transition: opacity 0.18s ease, transform 0.18s ease;
}

.nav-link:hover,
.nav-link.active {
  color: #b08a4a;
}

.nav-link:hover::after,
.nav-link.active::after {
  opacity: 1;
  transform: scaleX(1);
}

.nav-label {
  font-weight: 500;
}

.nav-caret {
  width: 0;
  height: 0;
  border-left: 4px solid transparent;
  border-right: 4px solid transparent;
  border-top: 5px solid rgba(0, 0, 0, 0.5);
  margin-left: 8px;
  transform: translateY(1px);
}

.topbar-right {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
}

.phone {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  text-decoration: none;
  color: #0c0b06;
  font-size: 12px;
  letter-spacing: 0.02em;
  white-space: nowrap;
  cursor: pointer;
}

.phone-icon {
  color: rgba(0, 0, 0, 0.55);
  display: inline-flex;
  align-items: center;
}

.phone:hover {
  color:#0c0b06;
}

@media (max-width: 720px) {
  .container {
    padding: 26px 12px;
  }

  .topbar-inner {
    height: auto;
    padding: 14px 0;
    position: relative;
    justify-content: center;
    align-items: center;
  }

  .nav-toggle {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    position: absolute;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
  }

  .nav {
    display: none;
  }


  .topbar-right {
    display: none;
  }
}
</style>

